import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  Component,
  AfterContentInit,
  AfterViewInit,
  TemplateRef,
  ElementRef,
  HostListener,
  ViewContainerRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  input,
  effect,
  contentChildren,
  viewChild,
} from '@angular/core';
import { TemplateDirective } from '@proget-shared';

@Component({
  imports: [CommonModule],
  selector: 'lib-limited-items',
  templateUrl: './limited-items.component.html',
  styleUrl: './limited-items.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('expandCollapse', [
      state('collapsed', style({ height: 0 })),
      state('expanded', style({ height: '*' })),
      transition('collapsed => expanded', [animate('0.5s ease-out')]),
      transition('expanded => collapsed', [animate('0.5s ease-in')]),
    ]),
    trigger('fadeInOut', [
      state('visible', style({ opacity: 1 })),
      state('hidden', style({ opacity: 0 })),
      transition('visible => hidden', [animate('0.2s ease-out')]),
      transition('hidden => visible', [animate('0.2s ease-in')]),
    ]),
  ],
})
export class LimitedElementsComponent implements AfterContentInit, AfterViewInit {
  public readonly items = input<any[]>([]);

  protected itemTemplateRef: TemplateRef<any> | undefined;
  protected isAllItemsVisible = false;
  protected displayedItems = [];
  protected hiddenItems = [];

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly hostElement = inject(ElementRef);
  private readonly viewContainer = inject(ViewContainerRef);
  private readonly templateDirectives = contentChildren(TemplateDirective);
  private readonly toggleButton = viewChild<ElementRef<HTMLElement>>('toggleButton');

  private _items = [];

  constructor() {
    effect(() => {
      this._items = this.items().slice();
      this.updateDisplayedItems();
    });
  }

  ngAfterContentInit(): void {
    this.itemTemplateRef = this.templateDirectives().find(({ appTemplate }) => appTemplate === 'item')?.templateRef;
  }

  ngAfterViewInit(): void {
    this.updateDisplayedItems();
  }

  @HostListener('window:resize')
  public onResize(): void {
    this.updateDisplayedItems();
  }

  protected toggleItemsVisibility(): void {
    this.isAllItemsVisible = !this.isAllItemsVisible;
    this.updateDisplayedItems();
  }

  protected updateDisplayedItems(): void {
    const visibleItemCount = this.calculateVisibleItemCount();

    this.displayedItems = this._items.slice(0, visibleItemCount);
    this.hiddenItems = this._items.slice(visibleItemCount);
    this.cdr.markForCheck();
  }

  private calculateVisibleItemCount(): number {
    const GAP_SIZE = 6; // 6px gap from scss
    const toggleButton = this.toggleButton();
    const toggleButtonWidth = toggleButton?.nativeElement.offsetWidth
      ? toggleButton.nativeElement.offsetWidth + GAP_SIZE
      : 80;
    const containerWidth = this.hostElement.nativeElement.offsetWidth - toggleButtonWidth;

    document.documentElement.style.setProperty('--toggle-button-width', `${toggleButtonWidth}px`);

    let totalItemWidth = GAP_SIZE;
    let visibleItemCount = 0;

    if (!this.itemTemplateRef) {
      return 0;
    }

    for (const item of this._items) {
      const itemWidth = this.getItemWidth(item) + GAP_SIZE;

      if (totalItemWidth + itemWidth > containerWidth) {
        break;
      }

      totalItemWidth += itemWidth;
      visibleItemCount++;
    }

    if (visibleItemCount === 0 && this._items.length > 0) {
      return 1;
    }

    return visibleItemCount;
  }

  private getItemWidth(item: any): number {
    const view = this.itemTemplateRef.createEmbeddedView({ $implicit: item });

    this.viewContainer.insert(view);
    view.detectChanges();
    const itemWidth = view.rootNodes[0].offsetWidth;

    view.destroy();

    return itemWidth;
  }
}
